import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';

import { apiJson } from './api';
import { useSEO } from './useSEO';

const MD_PLUGINS_REMARK = [remarkGfm, remarkMath];
const MD_PLUGINS_REHYPE = [rehypeKatex];

function MD({ children }) {
  if (!children) return null;
  return (
    <ReactMarkdown remarkPlugins={MD_PLUGINS_REMARK} rehypePlugins={MD_PLUGINS_REHYPE}>
      {String(children)}
    </ReactMarkdown>
  );
}

export default function NotesBrowser() {
  const [params, setParams] = useSearchParams();
  const [levels, setLevels] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [boards, setBoards] = useState([]);
  const [overview, setOverview] = useState({ spec: null, topics: [] });
  const [activeTopic, setActiveTopic] = useState(null);
  const [detail, setDetail] = useState(null);
  const [view, setView] = useState('student'); // 'student' | 'ai'
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const level = params.get('level') || '';
  const subject = params.get('subject') || '';
  const board = params.get('board') || '';

  useSEO({
    title: subject && board ? `${subject} ${board} notes` : 'Revision notes',
    description: 'Browse topic-by-topic revision notes generated from official exam-board specifications.',
    canonicalPath: '/notes',
  });

  useEffect(() => { apiJson('/api/levels').then((d) => setLevels(d || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (!level) { setSubjects([]); return; }
    apiJson(`/api/subjects/${encodeURIComponent(level)}`).then((d) => setSubjects(d || [])).catch(() => setSubjects([]));
  }, [level]);
  useEffect(() => {
    if (!level || !subject) { setBoards([]); return; }
    apiJson(`/api/boards/${encodeURIComponent(level)}/${encodeURIComponent(subject)}`).then((d) => setBoards(d || [])).catch(() => setBoards([]));
  }, [level, subject]);

  useEffect(() => {
    if (!level || !subject || !board) {
      setOverview({ spec: null, topics: [] });
      setActiveTopic(null);
      setDetail(null);
      return;
    }
    setBusy(true); setError('');
    apiJson(`/api/notes/overview/${encodeURIComponent(level)}/${encodeURIComponent(subject)}/${encodeURIComponent(board)}`)
      .then((data) => {
        setOverview({ spec: data.spec, topics: data.topics || [] });
        const first = (data.topics || [])[0];
        setActiveTopic((prev) => prev || first?.id || null);
      })
      .catch((err) => setError(err.message || 'Failed to load notes'))
      .finally(() => setBusy(false));
  }, [level, subject, board]);

  useEffect(() => {
    if (!activeTopic) { setDetail(null); return; }
    setBusy(true);
    apiJson(`/api/notes/topic/${encodeURIComponent(activeTopic)}`)
      .then(setDetail)
      .catch((err) => setError(err.message || 'Failed to load note'))
      .finally(() => setBusy(false));
  }, [activeTopic]);

  const grouped = useMemo(() => buildTree(overview.topics || []), [overview.topics]);

  const setField = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    if (key === 'level') { next.delete('subject'); next.delete('board'); }
    if (key === 'subject') { next.delete('board'); }
    setParams(next);
  };

  return (
    <div style={styles.page}>
      <div style={styles.hero}>
        <div>
          <h1 style={styles.h1}>Revision notes</h1>
          <p style={styles.subtle}>Topic-by-topic notes generated against the official exam-board specification.</p>
        </div>
        {overview.spec && (
          <div style={styles.meta}>
            {overview.spec.subject} · {overview.spec.board} · {overview.spec.version || 'current spec'}
            {overview.spec.sourceUrl && (
              <> · <a href={overview.spec.sourceUrl} target="_blank" rel="noreferrer" style={styles.link}>spec source</a></>
            )}
          </div>
        )}
      </div>

      <div style={styles.filters}>
        <Select label="Level" value={level} options={levels} onChange={(v) => setField('level', v)} />
        <Select label="Subject" value={subject} options={subjects} onChange={(v) => setField('subject', v)} />
        <Select label="Board" value={board} options={boards} onChange={(v) => setField('board', v)} />
      </div>

      {error && <p style={{ color: '#b91c1c' }}>{error}</p>}

      {!busy && level && subject && board && !overview.spec && (
        <div style={styles.empty}>No notes published yet for this subject/board.</div>
      )}

      {overview.spec && (
        <div style={styles.grid}>
          <aside style={styles.sidebar}>
            <div style={styles.sidebarTitle}>Topics ({overview.topics.length})</div>
            <TopicTree nodes={grouped} activeTopic={activeTopic} onSelect={setActiveTopic} />
          </aside>
          <main style={styles.main}>
            {busy && !detail && <div style={styles.subtle}>Loading…</div>}
            {detail ? (
              <>
                <NoteHeader detail={detail} view={view} setView={setView} />
                {view === 'student' ? <StudentView notes={detail.studentNotes} /> : <AiView notes={detail.aiNotes} statements={detail.statements} />}
              </>
            ) : (!busy && <div style={styles.empty}>Pick a topic from the left.</div>)}
          </main>
        </div>
      )}
    </div>
  );
}

function NoteHeader({ detail, view, setView }) {
  return (
    <header style={styles.noteHeader}>
      <div>
        <div style={styles.noteCode}>{detail.code || 'Topic'}</div>
        <h2 style={{ margin: '4px 0 8px' }}>{detail.title}</h2>
        <div style={styles.updated}>
          Updated {detail.notesUpdatedAt ? new Date(detail.notesUpdatedAt).toLocaleString() : 'recently'}
        </div>
      </div>
      <div style={styles.tabs} role="tablist">
        <TabBtn active={view === 'student'} onClick={() => setView('student')}>Student notes</TabBtn>
        <TabBtn active={view === 'ai'} onClick={() => setView('ai')} disabled={!detail.aiNotes}>AI tutor notes</TabBtn>
      </div>
    </header>
  );
}

function TabBtn({ active, onClick, children, disabled }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      disabled={disabled}
      style={{ ...styles.tabBtn, ...(active ? styles.tabBtnActive : null), ...(disabled ? styles.tabBtnDisabled : null) }}
    >
      {children}
    </button>
  );
}

function StudentView({ notes }) {
  if (!notes) return <div style={styles.empty}>No student notes for this topic.</div>;
  return (
    <article>
      {notes.summary && (
        <div style={styles.summary}><MD>{notes.summary}</MD></div>
      )}

      {Array.isArray(notes.key_formulas) && notes.key_formulas.length > 0 && (
        <Section title="Key formulas">
          <ul style={styles.list}>
            {notes.key_formulas.map((f, i) => (
              <li key={i}><strong>{f.name}:</strong>{' '}<MD>{`$${f.latex}$`}</MD></li>
            ))}
          </ul>
        </Section>
      )}

      {(notes.sections || []).map((section, idx) => (
        <Section key={idx} title={section.heading}>
          <MD>{section.body_md}</MD>

          {Array.isArray(section.key_terms) && section.key_terms.length > 0 && (
            <div style={styles.cardWrap}>
              {section.key_terms.map((t, i) => (
                <div key={i} style={styles.termCard}>
                  <div style={styles.termName}>{t.term}</div>
                  <div style={styles.termDef}><MD>{t.definition}</MD></div>
                </div>
              ))}
            </div>
          )}

          {Array.isArray(section.callouts) && section.callouts.length > 0 && (
            <div style={styles.calloutWrap}>
              {section.callouts.map((c, i) => (
                <div key={i} style={{ ...styles.callout, ...calloutStyle(c.kind) }}>
                  <div style={styles.calloutLabel}>{labelForKind(c.kind)}</div>
                  <div><MD>{c.text}</MD></div>
                </div>
              ))}
            </div>
          )}

          {Array.isArray(section.worked_examples) && section.worked_examples.length > 0 && (
            <div style={{ marginTop: 12 }}>
              {section.worked_examples.map((ex, i) => (
                <div key={i} style={styles.example}>
                  <div style={styles.exampleQ}><MD>{ex.prompt}</MD></div>
                  <MD>{ex.solution_md}</MD>
                </div>
              ))}
            </div>
          )}
        </Section>
      ))}

      {Array.isArray(notes.summary_bullets) && notes.summary_bullets.length > 0 && (
        <Section title="Quick recap">
          <ul style={styles.list}>
            {notes.summary_bullets.map((b, i) => <li key={i}><MD>{b}</MD></li>)}
          </ul>
        </Section>
      )}
    </article>
  );
}

function AiView({ notes, statements }) {
  if (!notes) return <div style={styles.empty}>No AI tutor notes for this topic.</div>;
  return (
    <article>
      <p style={styles.subtle}>
        This view shows the structured context our AI tutor and AI marker load when helping with this topic.
      </p>

      {Array.isArray(statements) && statements.length > 0 && (
        <Section title="Spec statements">
          <ol style={styles.list}>
            {statements.map((s, i) => <li key={i}>{s}</li>)}
          </ol>
        </Section>
      )}

      {Array.isArray(notes.learning_objectives) && notes.learning_objectives.length > 0 && (
        <Section title="Learning objectives">
          <ul style={styles.list}>
            {notes.learning_objectives.map((o, i) => <li key={i}><MD>{o}</MD></li>)}
          </ul>
        </Section>
      )}

      {Array.isArray(notes.key_concepts) && notes.key_concepts.length > 0 && (
        <Section title="Key concepts">
          <div style={styles.cardWrap}>
            {notes.key_concepts.map((c, i) => (
              <div key={i} style={styles.conceptCard}>
                <div style={styles.termName}>{c.name}</div>
                <div><MD>{c.explanation}</MD></div>
                {c.why_it_matters && (
                  <div style={styles.metaSmall}><strong>Why it matters:</strong> <MD>{c.why_it_matters}</MD></div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {Array.isArray(notes.common_misconceptions) && notes.common_misconceptions.length > 0 && (
        <Section title="Common misconceptions">
          {notes.common_misconceptions.map((m, i) => (
            <div key={i} style={{ ...styles.callout, ...calloutStyle('common_mistake'), marginBottom: 8 }}>
              <div style={styles.calloutLabel}>Misconception</div>
              <MD>{m.misconception}</MD>
              <div style={{ ...styles.metaSmall, marginTop: 6 }}><strong>Correction:</strong> <MD>{m.correction}</MD></div>
            </div>
          ))}
        </Section>
      )}

      {Array.isArray(notes.worked_examples) && notes.worked_examples.length > 0 && (
        <Section title="Worked examples">
          {notes.worked_examples.map((ex, i) => (
            <div key={i} style={styles.example}>
              <div style={styles.exampleQ}><MD>{ex.question}</MD></div>
              {Array.isArray(ex.answer_steps) && (
                <ol style={styles.list}>
                  {ex.answer_steps.map((step, si) => <li key={si}><MD>{step}</MD></li>)}
                </ol>
              )}
              {ex.final_answer && (
                <div style={styles.metaSmall}><strong>Final answer:</strong> <MD>{ex.final_answer}</MD></div>
              )}
            </div>
          ))}
        </Section>
      )}

      {Array.isArray(notes.marking_guidance) && notes.marking_guidance.length > 0 && (
        <Section title="Marking guidance">
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Command word</th>
                <th style={styles.th}>What to credit</th>
                <th style={styles.th}>Typical pitfalls</th>
              </tr>
            </thead>
            <tbody>
              {notes.marking_guidance.map((g, i) => (
                <tr key={i}>
                  <td style={styles.td}>{g.command_word}</td>
                  <td style={styles.td}><MD>{g.what_to_credit}</MD></td>
                  <td style={styles.td}><MD>{g.typical_pitfalls}</MD></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {Array.isArray(notes.probing_questions) && notes.probing_questions.length > 0 && (
        <Section title="Probing questions">
          <ul style={styles.list}>
            {notes.probing_questions.map((q, i) => <li key={i}><MD>{q}</MD></li>)}
          </ul>
        </Section>
      )}

      {Array.isArray(notes.prerequisites) && notes.prerequisites.length > 0 && (
        <Section title="Prerequisites">
          <ul style={styles.list}>
            {notes.prerequisites.map((p, i) => <li key={i}>{p}</li>)}
          </ul>
        </Section>
      )}
    </article>
  );
}

function Section({ title, children }) {
  return (
    <section style={styles.block}>
      <h3 style={styles.sectionH}>{title}</h3>
      <div className="md-body">{children}</div>
    </section>
  );
}

function Select({ label, value, options, onChange }) {
  return (
    <label style={styles.selectWrap}>
      <span style={styles.selectLabel}>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={styles.select}>
        <option value="">Choose…</option>
        {options.map((option) => <option key={option} value={option}>{option}</option>)}
      </select>
    </label>
  );
}

function TopicTree({ nodes, activeTopic, onSelect }) {
  if (!nodes?.length) return null;
  return (
    <ul style={styles.tree}>
      {nodes.map((node) => (
        <li key={node.id}>
          {node.hasNotes ? (
            <button
              type="button"
              onClick={() => onSelect(node.id)}
              style={{ ...styles.treeBtn, ...(activeTopic === node.id ? styles.treeBtnActive : null) }}
            >
              {node.code ? <span style={styles.treeCode}>{node.code}</span> : null}
              <span>{node.title}</span>
            </button>
          ) : (
            <div style={{ ...styles.treeBtn, opacity: 0.55, cursor: 'default' }}>
              {node.code ? <span style={styles.treeCode}>{node.code}</span> : null}
              <span>{node.title}</span>
            </div>
          )}
          {node.children?.length > 0 && (
            <div style={{ paddingLeft: 12 }}>
              <TopicTree nodes={node.children} activeTopic={activeTopic} onSelect={onSelect} />
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

function buildTree(flat) {
  const byId = new Map(flat.map((t) => [t.id, { ...t, children: [] }]));
  const roots = [];
  for (const t of flat) {
    const node = byId.get(t.id);
    if (t.parentId && byId.has(t.parentId)) byId.get(t.parentId).children.push(node);
    else roots.push(node);
  }
  return roots;
}

function labelForKind(kind) {
  return ({
    key_idea: 'Key idea',
    common_mistake: 'Common mistake',
    tip: 'Tip',
    warning: 'Watch out',
  })[kind] || 'Note';
}

function calloutStyle(kind) {
  return ({
    key_idea: { borderColor: '#0f766e', background: 'rgba(15,118,110,0.08)' },
    common_mistake: { borderColor: '#b45309', background: 'rgba(180,83,9,0.08)' },
    tip: { borderColor: '#1d4ed8', background: 'rgba(29,78,216,0.08)' },
    warning: { borderColor: '#b91c1c', background: 'rgba(185,28,28,0.08)' },
  })[kind] || { borderColor: '#475569', background: 'rgba(71,85,105,0.08)' };
}

const styles = {
  page: { padding: '24px clamp(16px, 4vw, 40px)', maxWidth: 1300, margin: '0 auto', color: 'var(--text)' },
  hero: { display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'end', flexWrap: 'wrap', marginBottom: 20 },
  h1: { margin: 0, fontSize: 34 },
  subtle: { color: 'var(--viewer-muted, #6b7280)' },
  meta: { color: 'var(--viewer-muted, #6b7280)', fontSize: 14 },
  link: { color: 'var(--accent, #1d4ed8)' },
  filters: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 },
  selectWrap: { display: 'grid', gap: 6 },
  selectLabel: { fontSize: 13, color: 'var(--viewer-muted, #6b7280)' },
  select: { padding: '10px 12px', borderRadius: 10, border: '1px solid var(--viewer-border, #e5e7eb)', background: 'var(--viewer-panel, #fff)', color: 'var(--text)' },
  grid: { display: 'grid', gridTemplateColumns: '320px 1fr', gap: 18 },
  sidebar: { background: 'var(--viewer-panel, #fff)', border: '1px solid var(--viewer-border, #e5e7eb)', borderRadius: 16, padding: 16, maxHeight: '78vh', overflow: 'auto', position: 'sticky', top: 12 },
  sidebarTitle: { fontWeight: 700, marginBottom: 12 },
  main: { background: 'var(--viewer-panel, #fff)', border: '1px solid var(--viewer-border, #e5e7eb)', borderRadius: 16, padding: 22 },
  tree: { listStyle: 'none', paddingLeft: 0, margin: 0, display: 'grid', gap: 4 },
  treeBtn: { display: 'flex', gap: 8, alignItems: 'baseline', width: '100%', textAlign: 'left', border: '1px solid transparent', borderRadius: 10, background: 'transparent', color: 'var(--text)', padding: '8px 10px', cursor: 'pointer', fontSize: 14 },
  treeBtnActive: { borderColor: '#1d4ed8', background: 'rgba(29,78,216,0.10)' },
  treeCode: { fontVariantNumeric: 'tabular-nums', color: 'var(--viewer-muted, #6b7280)', fontSize: 12, minWidth: 50 },
  noteHeader: { display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'start', flexWrap: 'wrap', borderBottom: '1px solid var(--viewer-border, #e5e7eb)', paddingBottom: 12, marginBottom: 16 },
  noteCode: { fontSize: 12, letterSpacing: 1.1, textTransform: 'uppercase', color: 'var(--viewer-muted, #6b7280)' },
  updated: { color: 'var(--viewer-muted, #6b7280)', fontSize: 13 },
  tabs: { display: 'inline-flex', gap: 4, padding: 4, background: 'rgba(148,163,184,0.12)', borderRadius: 10 },
  tabBtn: { border: 'none', background: 'transparent', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', color: 'var(--text)', fontWeight: 600, fontSize: 14 },
  tabBtnActive: { background: 'var(--viewer-panel, #fff)', boxShadow: '0 1px 2px rgba(0,0,0,0.08)' },
  tabBtnDisabled: { opacity: 0.45, cursor: 'not-allowed' },
  summary: { fontSize: 17, lineHeight: 1.65, padding: '12px 14px', background: 'rgba(15,118,110,0.06)', borderLeft: '3px solid #0f766e', borderRadius: 8, marginBottom: 8 },
  block: { marginTop: 22 },
  sectionH: { margin: '0 0 8px', fontSize: 18 },
  list: { paddingLeft: 22, lineHeight: 1.7, margin: '6px 0' },
  cardWrap: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10, marginTop: 12 },
  termCard: { border: '1px solid var(--viewer-border, #e5e7eb)', borderRadius: 12, padding: 12, background: 'rgba(148,163,184,0.06)' },
  termName: { fontWeight: 700, marginBottom: 4 },
  termDef: { fontSize: 14, lineHeight: 1.5 },
  conceptCard: { border: '1px solid var(--viewer-border, #e5e7eb)', borderRadius: 12, padding: 14, background: 'var(--viewer-panel, #fff)' },
  metaSmall: { fontSize: 13, color: 'var(--viewer-muted, #6b7280)', marginTop: 6 },
  calloutWrap: { display: 'grid', gap: 10, marginTop: 12 },
  callout: { border: '1px solid', borderRadius: 12, padding: 12 },
  calloutLabel: { fontSize: 11, letterSpacing: 1.1, textTransform: 'uppercase', fontWeight: 700, marginBottom: 4 },
  example: { borderLeft: '4px solid #0f766e', paddingLeft: 14, marginTop: 12, paddingBottom: 6 },
  exampleQ: { fontWeight: 600, marginBottom: 6 },
  table: { width: '100%', borderCollapse: 'collapse', marginTop: 8, fontSize: 14 },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid var(--viewer-border, #e5e7eb)', fontWeight: 700 },
  td: { padding: '8px 10px', borderBottom: '1px solid var(--viewer-border, #e5e7eb)', verticalAlign: 'top' },
  empty: { padding: 28, border: '1px dashed var(--viewer-border, #e5e7eb)', borderRadius: 16, textAlign: 'center', color: 'var(--viewer-muted, #6b7280)' },
};
